import type { ReactNode } from "react";
import { Wallet } from "lucide-react";
import type { Account } from "../../../types";
import { money } from "../../../lib/finance";
import { position } from "../overview-data";
import { Private, usePrivacy } from "../../../components/Privacy";

/** "The bigger picture": cash and savings per currency against card debt. */
export default function NetPosition({ selectedAccounts, usdRate, collapsed, collapseButton }: {
  selectedAccounts: Account[];
  usdRate: number | undefined;
  collapsed: boolean;
  /** The module's collapse toggle, drawn by the caller so every module's matches. */
  collapseButton: ReactNode;
}) {
  const { debt, net, byCurrency: cashByCurrency } = position(selectedAccounts);
  const { hidden } = usePrivacy();
  return (
    <section className={`position-card overview-module${collapsed ? " is-collapsed" : ""}`}>
      <div className="section-heading">
        <h2>The bigger picture</h2>
        <div className="overview-heading-actions"><strong className="collapsed-summary"><Private>{money(net)}</Private></strong><Wallet size={19} />{collapseButton}</div>
      </div>
      {(["MVR", "USD"] as const).map((currency) => (
        <div className="position-line" key={currency}>
          <span>
            <i className="dot mint" />
            Cash & savings ({currency})
          </span>
          <strong>
            <Private>{money(cashByCurrency(currency), currency)}</Private>
          </strong>
        </div>
      ))}
      <div className="position-line">
        <span>
          <i className="dot lavender" />
          Credit card debt
        </span>
        <strong><Private>{money(debt)}</Private></strong>
      </div>
      <div className="net-position">
        <span>
          Net position
          <small>Cash & savings minus card debt</small>
        </span>
        <strong
          className={
            !hidden && net !== null && net < 0 ? "negative" : ""
          }
        >
          <Private>{money(net)}</Private>
        </strong>
      </div>
      <p className="currency-note">
        Debt & net position in MVR · USD at{" "}
        {usdRate ?? "unavailable"}
      </p>
    </section>
  );
}
